/**
 * Navigasi adaptif: bottom tab bar di layar sempit, sidebar di layar lebar.
 *
 * Keduanya memakai item yang SAMA dari satu array NAV, jadi tidak mungkin
 * mereka berbeda isi — masalah klasik navigasi duplikat. Yang membedakan hanya
 * cara merender (ikon di atas label vs. baris dengan ikon di kiri).
 *
 * Bentuk 3D:
 * - Tab bar: item aktif "timbul" — pill ber-glow yang naik translateZ, dengan
 *   ikon lebih besar. Ini penanda utama di layar kecil, karena warna teks saja
 *   terlalu lemah di layar 5 inci.
 * - Sidebar: baris aktif punya tepi cahaya + penanda batang yang muncul dari
 *   kiri, memberi kesan tombol fisik yang sedang ditekan.
 *
 * Ikon memakai SVG (components/NavIcon.tsx), bukan emoji, supaya bentuknya sama
 * di semua sistem operasi dan bisa mengikuti warna tema.
 *
 * Aturan main: item berlabel panjang seperti "Kontributor" dipotong di tab bar
 * supaya 5 item muat di 320px tanpa ikon bergeser. Di sidebar label penuh.
 */

import { NavLink, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import NavIcon from './NavIcon'
import type { IconName } from './NavIcon'

/** Satu item navigasi. */
export interface NavItem {
  to: string
  /** Label penuh, dipakai di sidebar dan aria-label. */
  label: string
  /** Label pendek untuk tab bar (maks 9 huruf supaya tidak wrap). */
  shortLabel: string
  icon: IconName
  /** Route persis yang dianggap aktif (default: prefix). */
  end?: boolean
}

/**
 * Lima item tab bar, urutan dan label persis seperti mockup: Home, Kamus,
 * Belajar, Kontributor, Profil.
 *
 * "Belajar" menunjuk /pelajar dan aktif selama pathname-nya di bawah /pelajar
 * (termasuk detail bahasa, topik, dan kuis) - pengguna yang sedang belajar
 * tidak kehilangan penanda aktifnya.
 */
export const NAV: NavItem[] = [
  { to: '/', label: 'Home', shortLabel: 'Home', icon: 'home', end: true },
  { to: '/kamus', label: 'Kamus', shortLabel: 'Kamus', icon: 'book', end: true },
  { to: '/pelajar', label: 'Belajar', shortLabel: 'Belajar', icon: 'cap' },
  { to: '/kontributor', label: 'Kontributor', shortLabel: 'Kontrib', icon: 'pen', end: true },
  { to: '/profil', label: 'Profil', shortLabel: 'Profil', icon: 'user' },
]

/**
 * Tentukan item aktif untuk sebuah pathname.
 *
 * Dipisah dari NavLink karena sidebar butuh ini untuk menandai group aktif
 * ("Pelajar Bahasa" menyorot semua sub-halaman) sementara tiap baris tetap
 * punya aturan sendiri. `NAV` lain dipakai untuk menandai apa pun.
 */
export function isActiveItem(item: NavItem, pathname: string): boolean {
  if (item.end) return pathname === item.to
  if (pathname === item.to) return true
  // Cocok untuk /pelajar/bahasa/jawa dan /kamus?..., tapi tidak untuk
  // /kamusxyz — batas segmen wajib.
  return pathname.startsWith(`${item.to}/`)
}

/**
 * Tab bar bawah (mobile). `pb-[env(safe-area-inset-bottom)]` supaya tidak
 * tertutup gesture bar iPhone.
 *
 * Sengaja tidak punya tilt 3D mengikuti kursor: di layar sentuh tidak ada kursor,
 *   yang tersisa hanyalah kartu yang nempel di posisi terakhir setelah tap.
 * Bentuk 3D di sini datang dari pill aktif yang benar-benar naik (translateZ),
 * bukan dari interaksi.
 */
export function BottomTabBar() {
  const { pathname } = useLocation()

  return (
    // `nav-float` (bukan `fixed inset-x-0`) supaya bar mengambang dengan jarak
    // dari tepi layar. Padding bawah menyisakan ruang untuk gesture bar iOS.
    <nav
      aria-label="Navigasi utama"
      className="nav-glass nav-float fixed z-40 pb-2 pt-1.5 md:hidden"
    >
      <ul className="mx-auto flex max-w-lg items-stretch">
        {NAV.map((item) => {
          const active = isActiveItem(item, pathname)
          return (
            <li key={item.to} className="flex-1">
              <NavLink
                to={item.to}
                end={item.end}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
                className={[
                  'group flex flex-col items-center gap-1 px-1 pb-2 pt-2.5',
                  'transition-[transform,color] duration-200 ease-out',
                  // Tekan saat disentuh: di perangkat sentuh tidak ada hover,
                  // jadi tanpa `:active` tombol ini tidak memberi umpan balik
                  // sama sekali. Skala ditekan sengaja kecil - untuk navigasi,
                  // umpan balik yang terlalu agresif terasa seperti salah ketuk.
                  'active:scale-95',
                  // Aktif = teks putih karena pill-nya sekarang gradasi hijau
                  // pekat, bukan hijau muda seperti sebelumnya. Warna teks lama
                  // (kebacaan di atas hijau pucat) jadi tidak terbaca.
                  active ? 'text-white' : 'text-[var(--kb-muted)]',
                ].join(' ')}
              >
                <span
                  className={[
                    'flex items-center justify-center rounded-xl transition-all duration-250 ease-out',
                    // Pill aktif: membesar, naik, dan bercahaya.
                    active
                      ? 'nav-pill-active h-9 w-14 px-2'
                      : 'h-9 w-14 group-hover:bg-[var(--kb-surface-2)]',
                  ].join(' ')}
                >
                  <NavIcon
                    name={item.icon}
                    size={active ? 22 : 20}
                    className="transition-transform duration-200"
                  />
                </span>
                <span
                  className={[
                    'max-w-full truncate text-[0.7rem] transition-colors',
                    active ? 'font-semibold' : 'font-medium',
                  ].join(' ')}
                >
                  {item.shortLabel}
                </span>
              </NavLink>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

/**
 * Sidebar (desktop). Hanya di `md` ke atas; shell mobile tidak merendernya,
 * jadi di layar sempit elemen ini tidak ada di DOM.
 */
export function SideNav({ footer }: { footer?: ReactNode }) {
  const { pathname } = useLocation()

  // Catatan: jangan menambah utility `bg-*` ke className <aside> di bawah.
  // `.nav-glass` berada di @layer components, dan utility Tailwind selalu menang
  // atas layer itu - latar kacanya akan tertimpa sehingga token
  // --kb-surface-nav tidak berlaku sama sekali.
  return (
    <aside className="nav-glass hidden w-60 shrink-0 flex-col border-r border-[var(--kb-border)] md:flex">
      {/*
        Logo memakai monogram ber-gradien, bukan teks polos. Ini bukan sekadar
        dekorasi: di sidebar yang semi transparan, teks logo jadi sulit dibaca
        saat aurora terang lewat di belakangnya.
      */}
      <div className="flex items-center gap-3 px-5 py-5">
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white shadow-sm nav-logo"
        >
          <NavIcon name="book" size={21} />
        </span>
        <span className="min-w-0">
          <span className="block font-serif text-lg font-semibold leading-tight tracking-tight">
            Kamus <span className="brand-text">Nusantara</span>
          </span>
          <span className="mt-0.5 block text-xs text-[var(--kb-muted)]">
            Warisan bahasa, lestari bersama.
          </span>
        </span>
      </div>

      <nav aria-label="Navigasi utama" className="flex-1 px-3">
        <ul className="flex flex-col gap-1">
          {NAV.map((item) => {
            const active = isActiveItem(item, pathname)
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  aria-current={active ? 'page' : undefined}
                  className={[
                    'nav-item group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium',
                    'transition-all duration-200 ease-out',
                    active ? 'nav-item-active text-[var(--kb-brand-chip-ink)]' : 'text-[var(--kb-ink)]',
                  ].join(' ')}
                >
                  {/*
                    Indikator aktif: batang cahaya di tepi kiri. Muncul dengan
                    `scaleY` supaya tumbuh dari tengah, bukan muncul mendadak -
                    detail kecil yang membuat transisi terasa disengaja, bukan
                    sekadar berubah karena nilai atributnya ditimpa.
                  */}
                  <span aria-hidden="true" className="nav-item-marker" />

                  <span
                    className={[
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-200',
                      active
                        ? 'bg-[var(--kb-brand-chip-bg)] nav-icon-active'
                        : 'group-hover:bg-[var(--kb-surface-2)]',
                    ].join(' ')}
                  >
                    <NavIcon name={item.icon} size={19} filled={active} />
                  </span>

                  <span className="transition-colors">{item.label}</span>
                </NavLink>
              </li>
            )
          })}
        </ul>
      </nav>

      {footer ? (
        <div className="border-t border-[var(--kb-border)] p-4">{footer}</div>
      ) : null}
    </aside>
  )
}
